import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/images/logos/burotimenobck.png';
import './AdminLayout.css';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, userData } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [badges, setBadges] = useState({
    commands: 0,
    comments: 0,
    messages: 0
  });
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    fetchBadgeCounts();
    
    // Refresh badges every 20 seconds
    const interval = setInterval(fetchBadgeCounts, 20000);
    return () => clearInterval(interval);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const fetchBadgeCounts = async () => {
    try {
      const { db } = await import('../../services/firebase/firebaseconfig');
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      
      // Fetch pending orders count
      const ordersRef = collection(db, 'orders');
      const pendingOrdersQuery = query(ordersRef, where('status', '==', 'pending'));
      const ordersSnapshot = await getDocs(pendingOrdersQuery);
      const pendingOrdersCount = ordersSnapshot.size;

      // Fetch pending comments count
      const commentsRef = collection(db, 'comments');
      const pendingCommentsQuery = query(commentsRef, where('status', '==', 'pending'));
      const commentsSnapshot = await getDocs(pendingCommentsQuery);
      const pendingCommentsCount = commentsSnapshot.size;

      // Fetch unread messages count
      const messagesRef = collection(db, 'contactMessages');
      const unreadMessagesQuery = query(messagesRef, where('status', '==', 'new'));
      const messagesSnapshot = await getDocs(unreadMessagesQuery);
      const unreadMessagesCount = messagesSnapshot.size;

      setBadges({
        commands: pendingOrdersCount,
        comments: pendingCommentsCount,
        messages: unreadMessagesCount
      });
    } catch (error) {
      console.error('Error fetching badge counts:', error);
    }
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Tableau de bord',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      ),
      path: '/admin',
      pageTitle: 'Tableau de bord',
      pageSubtitle: 'Vue d\'ensemble de votre activité'
    },
    {
      id: 'commands',
      label: 'Commandes',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="9" cy="21" r="1"/>
          <circle cx="20" cy="21" r="1"/>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
        </svg>
      ),
      path: '/admin/commands',
      badge: badges.commands,
      pageTitle: 'Commandes',
      pageSubtitle: 'Gérer les commandes clients'
    },
    {
      id: 'products',
      label: 'Produits',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
        </svg>
      ),
      path: '/admin/products',
      pageTitle: 'Produits',
      pageSubtitle: 'Gérer votre catalogue de produits'
    },
    {
      id: 'categories',
      label: 'Catégories',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      ),
      path: '/admin/categories',
      pageTitle: 'Catégories',
      pageSubtitle: 'Organiser vos catégories de produits'
    },
    {
      id: 'home-products',
      label: 'Produits Accueil',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
      path: '/admin/home-products',
      pageTitle: 'Produits Accueil',
      pageSubtitle: 'Produits mis en avant sur la page d\'accueil'
    },
    {
      id: 'comments',
      label: 'Commentaires',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
      path: '/admin/comments',
      badge: badges.comments,
      pageTitle: 'Commentaires',
      pageSubtitle: 'Modérer les avis clients'
    },
    {
      id: 'messages',
      label: 'Messages',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      ),
      path: '/admin/messages',
      badge: badges.messages,
      pageTitle: 'Messages',
      pageSubtitle: 'Messages de contact des clients'
    },/*
    {
      id: 'invoices',
      label: 'Factures',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      ),
      path: '/admin/invoices',
      pageTitle: 'Factures',
      pageSubtitle: 'Gérer les factures et paiements'
    },*/
    {
      id: 'settings',
      label: 'Paramètres',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v6m0 6v6m-9-9h6m6 0h6" />
          <path d="M4.22 4.22l4.24 4.24m7.08 0l4.24-4.24M4.22 19.78l4.24-4.24m7.08 0l4.24 4.24" />
        </svg>
      ),
      path: '/admin/settings',
      pageTitle: 'Paramètres',
      pageSubtitle: 'Configuration du système'
    }
  ];

  const getCurrentPageInfo = () => {
    const currentItem = menuItems.find(item => item.path === location.pathname);
    return currentItem || { 
      pageTitle: 'Administration', 
      pageSubtitle: 'Panneau d\'administration',
      badge: null
    };
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
      setIsLoggingOut(true);
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error('Logout error:', error);
        alert('Erreur lors de la déconnexion');
      } finally {
        setIsLoggingOut(false);
      }
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (userData?.displayName) {
      return userData.displayName.charAt(0).toUpperCase();
    }
    if (userData?.email) {
      return userData.email.charAt(0).toUpperCase();
    }
    return 'A';
  };

  const currentPageInfo = getCurrentPageInfo();

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src={logo} alt="Burotime" />
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="nav-list">
            {menuItems.map(item => (
              <li key={item.id} className="nav-item">
                <button
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  data-tooltip={item.label}
                  aria-label={item.label}
                >
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="nav-badge">{item.badge}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <button 
            className="logout-btn" 
            onClick={handleLogout}
            disabled={isLoggingOut}
            aria-label="Déconnexion"
          >
            {isLoggingOut ? (
              <>
                <div className="spinner-small" />
                {!sidebarCollapsed && <span>Déconnexion...</span>}
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                {!sidebarCollapsed && <span>Déconnexion</span>}
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="admin-main">
        {/* Top Header */}
        <header className="admin-header">
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          {/* Page Title Section */}
          <div className="header-title-section">
            <h1 className="header-page-title">
              {currentPageInfo.pageTitle}
              {currentPageInfo.badge > 0 && (
                <span className="header-badge">
                  <span className="header-badge-dot"></span>
                  {currentPageInfo.badge} {currentPageInfo.badge === 1 ? 'nouveau' : 'nouveaux'}
                </span>
              )}
            </h1>
            {currentPageInfo.pageSubtitle && (
              <p className="header-page-subtitle">{currentPageInfo.pageSubtitle}</p>
            )}
          </div>
          <div className="header-actions">
            <button className="header-action-btn" aria-label="Notifications">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {(badges.commands + badges.comments + badges.messages) > 0 && (
                <span className="notification-badge">
                  {badges.commands + badges.comments + badges.messages}
                </span>
              )}
            </button>

            <div className="header-user">
              <div className="user-avatar">
                {getUserInitials()}
              </div>
              <div className="user-info">
                <span className="user-name">
                  {userData?.displayName || userData?.email?.split('@')[0] || 'Admin'}
                </span>
                <span className="user-role">
                  {userData?.role === 'super-admin' ? 'Super Admin' : 'Administrateur'}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="admin-content">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default AdminLayout;